import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./styles.module.css";

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const Dropdown = ({
  value,
  onChange,
  options = [],
  label,
  placeholder = "Select...",
  disabled,
  id,
  className,
  triggerClassName,
  "aria-label": ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedOption = options.find(
    (opt) => String(opt.value) === String(value)
  );
  const displayLabel = selectedOption ? selectedOption.label : placeholder;
  const isPlaceholder = !selectedOption;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleTriggerClick = () => {
    if (!disabled) setOpen((prev) => !prev);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") setOpen(false);
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleTriggerClick();
    }
  };

  const handleOptionClick = (option) => {
    onChange(option.value);
    setOpen(false);
  };

  const isSelected = (option) => String(option.value) === String(value);

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${className || ""}`.trim()}
    >
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <button
        id={id}
        type="button"
        className={`${styles.trigger} ${open ? styles.open : ""} ${disabled ? styles.disabled : ""} ${triggerClassName || ""}`.trim()}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || label || "Dropdown"}
        aria-labelledby={label ? undefined : id}
      >
        <span
          className={`${styles.triggerValue} ${isPlaceholder ? styles.triggerPlaceholder : ""}`.trim()}
        >
          {displayLabel}
        </span>
        <span className={styles.chevron} aria-hidden>
          <ChevronIcon />
        </span>
      </button>
      {open && (
        <div
          className={styles.panel}
          role="listbox"
          aria-activedescendant={value ? `option-${value}` : undefined}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              id={`option-${option.value}`}
              aria-selected={isSelected(option)}
              className={`${styles.option} ${isSelected(option) ? styles.selected : ""}`.trim()}
              onClick={() => handleOptionClick(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
  triggerClassName: PropTypes.string,
  "aria-label": PropTypes.string,
};

export default Dropdown;
